import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../services/api';
import { getSupportedAudioRecorderFormat } from '../utils/mediaRecorderUtils';
import './VoiceReRecord.css';

/**
 * Minimal voice re-record widget for Profile page.
 * Uses MediaRecorder and uploads to /api/v1/onboarding/voice/re-record
 * @param {Object} props
 * @param { (recording: boolean) => void } [props.onRecordingChange] - Called when recording starts (true) or stops (false)
 */
function VoiceReRecord({ onRecordingChange }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (timerRef.current) clearInterval(timerRef.current);
      onRecordingChange?.(false);
    };
  }, [audioUrl, onRecordingChange]);

  const fetchVoiceStatus = async () => {
    try {
      const res = await apiClient.get('/api/v1/onboarding/voice');
      setStatus(res.data);
    } catch (e) {
      // Non-blocking in profile
      setStatus(null);
    }
  };

  useEffect(() => {
    fetchVoiceStatus();
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      setSuccess(false);
      const { mimeType, extension } = getSupportedAudioRecorderFormat();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blobType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      onRecordingChange?.(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
    } catch (e) {
      setError('Microphone permission is required to record voice.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onRecordingChange?.(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const reset = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setRecordingTime(0);
    setError(null);
    setSuccess(false);
  };

  const upload = async () => {
    if (!audioBlob) return;
    if (recordingTime < 30) {
      setError('Please record at least 30 seconds (required for AI voice clone).');
      return;
    }
    try {
      setUploading(true);
      setError(null);
      setSuccess(false);
      const { extension } = getSupportedAudioRecorderFormat();
      const formData = new FormData();
      formData.append('voice_file', audioBlob, `voice.${extension}`);
      await apiClient.post('/api/v1/onboarding/voice/re-record', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
      await fetchVoiceStatus();
      reset();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to upload voice. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-rerecord-card">
      <div className="voice-rerecord-header">
        <h2>Voice sample</h2>
        <p>Re-record your voice sample (minimum 30 seconds) for agent personalization and AI voice clone.</p>
        {status?.voice_recorded && (
          <div className="voice-rerecord-meta">
            <span className="pill">On file</span>
          </div>
        )}
      </div>

      {!audioUrl ? (
        <div className="voice-rerecord-actions">
          {!isRecording ? (
            <button type="button" className="primary" onClick={startRecording}>
              Start recording
            </button>
          ) : (
            <div className="recording-row">
              <span className="recording-dot" />
              <span className="recording-time">{formatTime(recordingTime)}</span>
              <button type="button" className="danger" onClick={stopRecording}>
                Stop
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="voice-rerecord-preview">
          <audio src={audioUrl} controls />
          <div className="voice-rerecord-buttons">
            <button type="button" className="secondary" onClick={reset} disabled={uploading}>
              Re-record
            </button>
            <button type="button" className="primary" onClick={upload} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          <div className="hint">Minimum duration: 30 seconds.</div>
        </div>
      )}

      {success && <div className="success">Voice sample updated.</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default VoiceReRecord;


